import { useState } from "react";
import { motion } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import AnimatedSection from "./AnimatedSection";
import { Calculator, TrendingDown, MessageCircle } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030";

const tiers = [
  { max: 2, price: 500 },
  { max: 5, price: 1200 },
  { max: 10, price: 2000 },
];

const getOkontainersPrice = (ops) => {
  if (ops <= 2) return 500;
  if (ops <= 5) return 1200;
  if (ops <= 10) return 2000;
  return Math.round(2000 + (ops - 10) * 180);
};

const SavingsCalculator = () => {
  const { lang } = useLang();
  const t = translations[lang].calculator;
  const [ops, setOps] = useState(3);

  const internalCost = 2800 + ops * 150;
  const okCost = getOkontainersPrice(ops);
  const savings = internalCost - okCost;
  const savingsPercent = Math.round((savings / internalCost) * 100);
  const yearlySavings = savings * 12;

  return (
    <section data-testid="calculator-section" className="bg-white py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <AnimatedSection>
          <div className="text-center mb-14">
            <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-semibold text-electric mb-4 font-body">
              {t.overline}
            </p>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-navy font-heading mb-4">
              {t.headline}
            </h2>
            <p className="text-lg text-slate-500 font-body max-w-xl mx-auto">{t.subheadline}</p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.15}>
          <div className="max-w-4xl mx-auto">
            <div className="bg-slate-50 rounded-3xl p-8 md:p-10 border border-slate-200">
              {/* Slider */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                  <label className="text-sm font-semibold text-navy font-heading">{t.opsLabel}</label>
                  <span className="text-2xl font-black text-electric font-heading">{ops}</span>
                </div>
                <input
                  data-testid="calculator-slider"
                  type="range"
                  min="1"
                  max="15"
                  value={ops}
                  onChange={(e) => setOps(Number(e.target.value))}
                  className="w-full h-2 bg-slate-200 rounded-full appearance-none cursor-pointer accent-electric"
                />
                <div className="flex justify-between text-xs text-slate-400 mt-2 font-body">
                  <span>1</span>
                  <span>5</span>
                  <span>10</span>
                  <span>15</span>
                </div>
              </div>

              {/* Results */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {/* Internal cost */}
                <div className="bg-white rounded-2xl p-6 border border-slate-200 text-center">
                  <p className="text-xs uppercase tracking-wider text-slate-400 mb-2 font-body">{t.internalLabel}</p>
                  <p className="text-3xl font-black text-slate-400 font-heading line-through decoration-red-400">
                    {internalCost.toLocaleString("es-ES")}<span className="text-lg">&euro;</span>
                  </p>
                  <p className="text-xs text-slate-400 mt-1 font-body">{t.perMonth}</p>
                </div>

                {/* OKontainers cost */}
                <div className="bg-navy rounded-2xl p-6 text-center shadow-lg">
                  <p className="text-xs uppercase tracking-wider text-electric-light mb-2 font-body">OKontainers</p>
                  <p className="text-3xl font-black text-white font-heading">
                    {okCost.toLocaleString("es-ES")}<span className="text-lg">&euro;</span>
                  </p>
                  <p className="text-xs text-slate-300 mt-1 font-body">{t.perMonth}</p>
                </div>

                {/* Savings */}
                <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200 text-center">
                  <p className="text-xs uppercase tracking-wider text-emerald-600 mb-2 font-body">{t.savingsLabel}</p>
                  <div className="flex items-center justify-center gap-1">
                    <TrendingDown size={20} className="text-emerald-600" />
                    <p className="text-3xl font-black text-emerald-600 font-heading">{savingsPercent}%</p>
                  </div>
                  <p className="text-xs text-emerald-600 mt-1 font-body font-semibold">
                    {yearlySavings.toLocaleString("es-ES")}&euro; {t.perYear}
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="text-center mt-8">
                <a
                  data-testid="calculator-cta"
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-electric text-white font-semibold py-3.5 px-8 rounded-full hover:bg-electric-hover transition-all shadow-lg shadow-electric/20 text-sm"
                >
                  <MessageCircle size={16} />
                  {t.cta}
                </a>
              </div>
            </div>

            <p className="text-center text-xs text-slate-400 mt-4 font-body italic">{t.disclaimer}</p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default SavingsCalculator;
